import React, { useEffect, useState } from 'react';
import { DataIndicators } from '../../../../../components/DataIndicators';
import { useLanguageLocale } from '../../../../../hooks/language-locale-hooks';
import { isDataLoaded, Data, DataState } from '../../../../../types/data-providers';
import { GenericToolbar } from '../../../../../components/GenericToolbar';
import { pushAppRoute } from '../../../../../data/domain/route-manager';
import { useSelectedSiteData, isRecordFound } from '../../../../../hooks/data-hooks';
import { useHistory } from 'react-router-dom';
import { ContentContainer } from '../../../../../components/ContentContainer';
import { RecordDataIndicators } from '../../../../../components/RecordDataIndicators';
import * as _ from 'lodash';
import { sortObjectArrayNaturally, compareNaturally } from '../../../../../lib';
import { ChannelDatum } from '../../../../../types';
import { ChannelDataListGroups } from '../../../../../components/ChannelDataListGroups';
import { OverviewSiteReadingDetailsMetricListItem } from '../../../../../components/OverviewSiteReadingDetailsMetricListItem';
import { useSiteChannelData } from '../../../../../data/DataProviders/SiteChannelDataProvider';
import { useAppRoute } from '../../../../../hooks/app-route-hooks';
import { getUpdateChannelDataWithDevideId } from '../../../../../lib/signalr-helper';
import { useSignalRSiteData } from '../../../../../data/DataProviders/SignalRSiteDataProvider';

export const OverviewSiteReadingDetails: React.FC = () => {
    const selectedSiteData = useSelectedSiteData();
    const currentAppRoute = useAppRoute('OverviewSiteReadings');
    const siteRealTimeData = useSignalRSiteData();
    const history = useHistory();
    const { t } = useLanguageLocale();
    const siteChannelData = useSiteChannelData();
    const [ChannelDetails, setChannelData] = useState<Data<ChannelDatum[]>>({ state: DataState.Loading });

    useEffect(() => {
        if (isDataLoaded(siteChannelData)) setChannelData(siteChannelData);
        if (siteRealTimeData && siteRealTimeData.channelData && siteRealTimeData.channelData.length > 0) {
            const clone = getUpdateChannelDataWithDevideId(siteRealTimeData.channelData, siteChannelData);
            setChannelData(clone);
        }
    }, [siteChannelData, siteRealTimeData]); // eslint-disable-line react-hooks/exhaustive-deps

    const channelData: ChannelDatum[] = isDataLoaded(ChannelDetails)
        ? sortObjectArrayNaturally(ChannelDetails.value, [{ sortField: 'deviceName' }, { sortField: 'channelName' }])
        : [];

    const channelDataGroups = isDataLoaded(ChannelDetails)
        ? _.groupBy(channelData, (m) => m.deviceName || t('DASHBOARD.SITE_DETAILS.READING_CARDS.UNKNOWN'))
        : {};

    const groups = Object.keys(channelDataGroups)
        .sort(compareNaturally)
        .map((groupName) => ({
            name: groupName,
            items: channelDataGroups[groupName],
        }));

    return (
        <>
            <GenericToolbar
                showBackButton
                backButtonOnClick={(): void => {
                    pushAppRoute(history, {
                        type: 'OverviewSite',
                        locationId: currentAppRoute.locationId,
                        siteId: currentAppRoute.siteId,
                    });
                }}
                title={t('DASHBOARD.SITE_DETAILS.ALL_DETAILS')}
                subtitle={isRecordFound(selectedSiteData) ? selectedSiteData.value.name : ''}
            />

            <ContentContainer>
                <RecordDataIndicators
                    recordData={selectedSiteData}
                    errorMessage={t('DASHBOARD.ERROR_LOADING_SITE')}
                    notFoundIcon="Business"
                    notFoundMessage={t('DASHBOARD.SITE_NOT_FOUND')}
                />

                {isRecordFound(selectedSiteData) && (
                    <>
                        <DataIndicators
                            errorMessage={t('DASHBOARD.ERROR_LOADING_DATA')}
                            emptyMessage={t('DASHBOARD.NO_DATA')}
                            data={siteChannelData}
                            emptyIcon="Devices"
                            isEmpty={channelData.length === 0}
                        />

                        {isDataLoaded(siteChannelData) && (
                            <ChannelDataListGroups
                                groups={groups}
                                component={OverviewSiteReadingDetailsMetricListItem}
                                componentTestId="site-reading-list-item"
                            />
                        )}
                    </>
                )}
            </ContentContainer>
        </>
    );
};
